import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { stockNumber } from "../utils/lineStock";
import { stockSupply, stockArrivalDate, stockReasonKey } from "../utils/stockSupply";

export function StockContextNote({ stock, locale }) {
  const { t } = useTranslation();
  if (stock.status !== "ok") return null;
  const shared = stock.requested_quantity_basis?.scope === "shared_product_total";
  const quantity = Object.hasOwn(stock, "remaining") ? stockNumber(stock.requested_quantity, locale) : null;
  const hasContext = quantity != null && typeof stock.requested_unit === "string" && stock.requested_unit.trim();
  if (!shared && !hasContext) return null;
  return <Box sx={{ mb: 1.5 }}>
    {shared && <Typography variant="caption" component="p" sx={{ m: 0, fontSize: 11, color: "primary.main", overflowWrap: "anywhere" }}>
      {t("stock.sharedProductTotal")}
    </Typography>}
    {hasContext && <Typography variant="caption" component="p" color="text.secondary" sx={{ m: 0, mt: shared ? 0.5 : 0, fontSize: 11, overflowWrap: "anywhere" }}>
      {t("stock.requested", { value: quantity, unit: stock.requested_unit })}
    </Typography>}
  </Box>;
}

export function StockReason({ stock }) {
  const { t } = useTranslation();
  if (stock.status !== "unavailable" || !stock.reason) return null;
  return <Typography variant="caption" component="p" color="text.secondary" sx={{ m: 0, mt: 0.75, fontSize: 11, overflowWrap: "anywhere" }}>
    {t(stockReasonKey(stock.reason))}
  </Typography>;
}

const tableSx = { "& .MuiTableCell-root": { px: 1.25, py: 0.9, fontSize: 12, overflowWrap: "anywhere", fontVariantNumeric: "tabular-nums" },
    "& thead th": { bgcolor: "action.hover", fontSize: 11, fontWeight: 600 }, "& tbody tr:last-child > *": { borderBottom: 0 } };
const frameSx = { border: "1px solid", borderColor: "divider", borderRadius: "10px", overflow: "hidden" };

/** Same warehouse table reused in the search result and the wider stock dialog. */
export function StockWarehouseDetails({ stock, locale, sx = {} }) {
  const { t } = useTranslation();
  const supply = stockSupply(stock);
  if (!supply?.warehousePresent) return null;
  const amount = value => stockNumber(value, locale) ?? t("stock.unknown");
  const heading = key => <Typography variant="subtitle2" component="h3" sx={{ fontSize: 12, fontWeight: 700, color: "primary.main", mb: 0.75 }}>{t(`stock.${key}`)}</Typography>;
  return <Box data-stock-warehouses sx={sx}>
      {heading("warehouses")}
      {supply.warehouses?.length ? <TableContainer sx={frameSx}><Table size="small" aria-label={t("stock.warehouses")} sx={tableSx}>
        <TableHead><TableRow><TableCell>{t("stock.warehouse")}</TableCell><TableCell align="right">{t("stock.physical")}</TableCell></TableRow></TableHead>
        <TableBody>{supply.warehouses.map((row, index) => <TableRow key={`${row.id ?? row.name}-${index}`}>
          <TableCell component="th" scope="row">{row.name}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>{amount(row.quantity)}{row.unit ? ` ${row.unit}` : ""}</TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer> : <Typography variant="caption" color="text.secondary">{t("stock.warehouseUnavailable")}</Typography>}
  </Box>;

}

export function StockCommitmentDetails({ stock, locale, sx = {} }) {
  const { t } = useTranslation();
  const supply = stockSupply(stock);
  if (!supply?.metrics.length) return null;
  const amount = value => stockNumber(value, locale) ?? t("stock.unknown");
  const heading = key => <Typography variant="subtitle2" component="h3" sx={{ fontSize: 12, fontWeight: 700, color: "primary.main", mb: 0.75 }}>{t(`stock.${key}`)}</Typography>;
  return <Box sx={sx}>
      {heading("ordersAndCommitments")}
      <TableContainer sx={frameSx}><Table size="small" aria-label={t("stock.ordersAndCommitments")} sx={tableSx}>
        <TableBody>{supply.metrics.map(row => <TableRow key={row.key}>
          <TableCell component="th" scope="row">{t(`stock.${row.key}`)}</TableCell>
          <TableCell align="right" data-stock-value={row.key} sx={{ fontWeight: 700 }}>{amount(row.value)}</TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer>
  </Box>;

}

/** Optional detail stays out of the compact hover preview. */
export function StockAdditionalDetails({ stock, locale, sx = {}, showWarehouses = true, showCommitments = true,
  showArrivals = true, showExplanation = true }) {
  const { t } = useTranslation();
  const supply = stockSupply(stock);
  if (!supply) return null;
  const amount = value => stockNumber(value, locale) ?? t("stock.unknown");
  const heading = key => <Typography variant="subtitle2" component="h3" sx={{ fontSize: 12, fontWeight: 700, color: "primary.main", mb: 0.75 }}>{t(`stock.${key}`)}</Typography>;
  return <Box data-stock-additional-detail sx={{ display: "grid", gap: 2, mt: 2, ...sx }}>
    {showCommitments && <StockCommitmentDetails stock={stock} locale={locale} />}
    {showWarehouses && <StockWarehouseDetails stock={stock} locale={locale} />}
    {showArrivals && (supply.ordersPresent || supply.countPresent || supply.multiplePresent || supply.nextArrivalPresent) && <Box>
      {heading("supplyArrivals")}
      {supply.countPresent && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontSize: 11 }}>
        {t("stock.purchaseOrdersCount", { value: amount(supply.count) })}
      </Typography>}
      {supply.multiplePresent && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontSize: 11 }}>
        {t("stock.multipleOrders", { value: supply.hasMultiple == null ? t("stock.unknown") : t(supply.hasMultiple ? "stock.yes" : "stock.no") })}
      </Typography>}
      {supply.orders?.length ? <TableContainer sx={frameSx}><Table size="small" aria-label={t("stock.supplyArrivals")} sx={{ ...tableSx, tableLayout: "fixed" }}>
        <TableHead><TableRow><TableCell sx={{ width: "37%" }}>{t("stock.expectedDate")}</TableCell><TableCell>{t("stock.purchaseOrder")}</TableCell><TableCell align="right">{t("common.quantity")}</TableCell></TableRow></TableHead>
        <TableBody>{supply.orders.map((row, index) => <TableRow key={index}>
          <TableCell component="th" scope="row">{stockArrivalDate(row.arrival, locale) ?? row.text ?? t("stock.noArrivalDate")}</TableCell>
          <TableCell>{row.number != null ? [row.number, row.year].filter(value => value != null).join(" / ") : "—"}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>{amount(row.quantity)}</TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer> : supply.breakdown
        ? <Typography variant="body2" sx={{ fontSize: 12, whiteSpace: "pre-line" }}>{supply.breakdown}</Typography>
        : supply.nextArrivalPresent && stockArrivalDate(supply.nextArrival, locale)
        ? <Typography variant="body2" sx={{ fontSize: 12 }}>{t("stock.nextArrival", { value: stockArrivalDate(supply.nextArrival, locale) })}</Typography>
        : <Typography variant="caption" color="text.secondary">{t("stock.noArrivalDate")}</Typography>}
    </Box>}
    {showExplanation && (supply.message || supply.level) && <Box component="details" sx={{ p: 1.5, borderRadius: "10px", bgcolor: "action.hover" }}>
      <Box component="summary" sx={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: "primary.main" }}>{t("stock.explanation")}</Box>
      {supply.message && <Typography variant="body2" sx={{ mt: 1, fontSize: 12, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{supply.message}</Typography>}
      {supply.level && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: supply.message ? 0.5 : 0 }}>{t("stock.level", { value: supply.level })}</Typography>}
    </Box>}
  </Box>;
}
